import { useEffect, useMemo, useRef, useState } from "react";
import { countryMatches } from "../build5/api5";

// Searchable country picker for the web upload form. Same vocabulary and the same search rule as
// Viewer Filters (web), Android and the backend: the list and aliases come from /api/countries;
// nothing is hard-coded here. Value semantics are unchanged from the former <select>: `value` is
// the selected country NAME (or ""), and onChange receives the new name — the caller still resolves
// the code from the name exactly as before.
export function CountryPicker({ countries, value, onChange, testId, placeholder = "Search a country…" }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);

  // Keep the field text in sync when the selected value changes from outside (e.g. after save).
  useEffect(() => { setQuery(value || ""); }, [value]);

  const matches = useMemo(() => {
    const q = query.trim();
    // The selected name shown in the box should not narrow the list to itself when reopened.
    const effective = value && q === value ? "" : q;
    return countries.filter((c) => countryMatches(c, effective)).slice(0, 60);
  }, [countries, query, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) close(); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  });

  const close = () => { setOpen(false); setQuery(value || ""); };
  const choose = (c) => { onChange(c.name); setQuery(c.name); setOpen(false); };
  const clear = () => { onChange(""); setQuery(""); setOpen(false); };

  const onInput = (e) => {
    const next = e.target.value;
    setQuery(next); setActive(0); setOpen(true);
    if (next.trim() === "" && value) onChange("");
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { if (open && matches[active]) { e.preventDefault(); choose(matches[active]); } }
    else if (e.key === "Escape") { close(); }
  };

  const listId = `${testId}-list`;
  return (
    <div className="country-picker" ref={rootRef} data-testid={`${testId}-picker`}>
      <input
        type="text" role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
        value={query} placeholder={placeholder} autoComplete="off"
        onChange={onInput} onFocus={() => setOpen(true)} onKeyDown={onKeyDown}
        data-testid={testId}
      />
      {value && (
        <button type="button" className="country-picker-clear" onClick={clear} aria-label="Clear country" data-testid={`${testId}-clear`}>×</button>
      )}
      {open && (
        <ul className="country-picker-list" role="listbox" id={listId} data-testid={listId}>
          {matches.length === 0 && <li className="country-picker-empty" data-testid={`${testId}-empty`}>No country matches “{query.trim()}”</li>}
          {matches.map((c, i) => (
            <li key={c.code} role="option" aria-selected={c.name === value}
              className={`country-picker-option ${i === active ? "active" : ""} ${c.name === value ? "selected" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); choose(c); }} onMouseEnter={() => setActive(i)}
              data-testid={`${testId}-option-${c.code}`}>
              <span>{c.name}</span>
              {c.parent_code && <span className="country-picker-parent">{(countries.find((p) => p.code === c.parent_code) || {}).name || c.parent_code}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
